import React, { useRef, useState, useEffect, InputHTMLAttributes } from 'react'
import { twMerge } from 'tailwind-merge'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  debounce?: number
  $size?: 'medium' | 'large'
}

export const Input: React.FC<InputProps> = ({
  debounce,
  $size = 'medium',
  type = 'text',
  value,
  defaultValue,
  onChange,
  disabled,
  className,
  ...rest
}) => {
  const [inputValue, setInputValue] = useState(defaultValue ?? '')
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const onChangeListener: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    if (value !== undefined) {
      // Controlled component
      onChange && onChange(e)
    } else {
      // Uncontrolled component with optional debounce
      setInputValue(e.target.value)
      if (typeof debounce === 'number') {
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current)
        }
        debounceTimeoutRef.current = setTimeout(() => {
          onChange && onChange(e)
        }, debounce)
      } else {
        onChange && onChange(e)
      }
    }
  }

  useEffect(() => {
    // If value changes externally, update inputValue for uncontrolled scenario
    if (value === undefined) {
      return
    }
    setInputValue(value)
  }, [value])

  return (
    <input
      type={type}
      value={value !== undefined ? value : inputValue}
      onChange={onChangeListener}
      disabled={disabled}
      className={twMerge(
        'bg-element text-foreground hover:border-hover rounded-lg border p-2 font-sans text-sm focus-visible:border-[color:var(--focus-ring-color)]',
        $size === 'large' ? 'px-4 py-3' : 'px-3 py-2',
        disabled ? 'pointer-events-none opacity-30' : '',
        className,
      )}
      {...rest}
    />
  )
}
